import _debounce from 'lodash.debounce';
import _orderBy from 'lodash.orderby';
import Vue from 'vue';
import Errors from '../classes/errors';
import { COUNTRIES } from '../consts/countries';
import { DropdownAccessibilityMixin } from '../mixins/dropdown-accessibility-mixin';
import { resolveBrandStoreSlugByRetailerId } from '../services/brand-store-retailer-id';
import {
    filterStoresByFrenchTerritory,
    geocodeWithFrenchFallback,
    getFrenchTerritoryCountry,
    isFrenchTerritoryScoped,
    isGeocodeResultInFrenchTerritory,
    resolveFrenchGeocodeParams,
    setCaptchaEuScript,
} from '../utils';

Vue.component('brandstore-search-component', {
    mixins: [DropdownAccessibilityMixin],

    props: {
        stores: {
            value: Object,
        },
        isParentGeocoderInit: {
            value: Boolean,
            default: false,
        },
        brandstoreResultAmount: {
            value: Number,
        },
        siteHandle: {
            value: String,
        },
        errors: {
            value: new Errors(),
        },
        inputId: {
            value: String,
        },
        defaultBrandstoreSlug: {
            type: String,
            default: null,
        },
        territory: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            selectedBrandStore: null,
            search: '',
            dropdownStore: null,
            countryHandle: '',
            retailerId: '',

            geocoder: null,

            // Dealer search (zip code vars)
            showDropdown: false,
            cityNotFound: false,
            store: '',
            zipCoords: null,
            searchQueryIsDirty: false,
            nearbyProximityStores: null,
            needMoreInput: false,
            apiRequestLoading: false,

            countryValue: COUNTRIES,
        };
    },

    computed: {
        moreThenTwoStores() {
            if (!this.countryHandle) {
                return false;
            }
            return Object.keys(this.stores).length > 2;
        },
        hasBrandStores() {
            if (!this.countryHandle) {
                return false;
            }
            return Object.keys(this.stores).length > 0;
        },
        orderedStores() {
            return _orderBy(this.stores, 'title');
        },

        isTerritoryScoped() {
            return (
                this.siteHandle === 'mgMotorsFrance' &&
                isFrenchTerritoryScoped(this.territory)
            );
        },

        searchableStores() {
            if (!this.isTerritoryScoped) {
                return this.stores;
            }

            return filterStoresByFrenchTerritory(this.stores, this.territory);
        },

        toggleClasses() {
            return `${this.handleShowContainerError()} ${this.isDropdownActive()}`;
        },

        toggleDropdownClasses() {
            return `${this.isDropdownActive()}`;
        },
    },

    watch: {
        store(value) {
            const isSearchBrandstoreInvalid = value.length < 3;
            const isSelectedBrandstoreEqualSearchBrandstore =
                this.selectedBrandStore &&
                this.selectedBrandStore.title === value;

            if (value.length === 0) {
                this.resetErrorState();
                return;
            }

            if (isSearchBrandstoreInvalid) {
                this.apiRequestLoading = false;
                this.needMoreInput = true;
                this.nearbyProximityStores = null;
                this.showDropdown = false;
                return;
            }

            if (isSelectedBrandstoreEqualSearchBrandstore) {
                return;
            }
            this.selectedBrandStore = null;
            this.needMoreInput = false;
            this.searchByZipGoogleApi();
        },

        selectedBrandStore(value) {
            this.showDropdown = false;
            if (!value) return;

            document.getElementById(this.inputId).value = this.store;

            EventBus.$emit('brandstore-selected', value);
            EventBus.$emit('brandstore-dropdown-value-selected', {
                store: JSON.stringify(value),
                inputId: this.inputId,
            });
        },

        countryHandle() {
            this.dropdownStore = null;
        },

        territory(value, previousValue) {
            if (!previousValue && this.isSelectedStoreSearchable()) {
                return;
            }

            this.resetSearch();
        },
    },

    mounted() {
        if (this.siteHandle !== 'default') {
            this.countryHandle = this.siteHandle;
        }
        setCaptchaEuScript();

        // When several dealer-search instances render on one page (e.g. the business/fleet
        // landing page with multiple form sections), Google Maps may already be loaded by the
        // time this instance mounts — in which case the 'google-maps-ready' event has already
        // fired and a $once listener would never run, leaving the geocoder null and the search
        // spinning forever. Initialise immediately when Maps is present; otherwise wait for the event.
        if (window.google && window.google.maps) {
            this.initMap();
        } else {
            EventBus.$once('google-maps-ready', this.initMap);

            // For cases where the parent component is not the configurator component.
            // Since configurator component needs to init geocoder that's used in configurator stock,
            // we need to ensure that google maps aren't injected more than once since it's creating bugs that way.
            if (!this.isParentGeocoderInit) {
                this.injectGoogleMaps();
            }
        }

        const searchParams = new URLSearchParams(window.location.search);
        this.retailerId = searchParams.get('retailerid') || '';

        this.preselectOneBrandstore();
    },

    methods: {
        isDropdownActive() {
            return this.zipCoords ? 'dropdown-active' : '';
        },

        handleShowContainerError() {
            return this.needMoreInput || this.cityNotFound
                ? 'new-input__dropdown-container--error'
                : '';
        },

        preselectOneBrandstore() {
            if (
                this.defaultBrandstoreSlug &&
                this.preselectBrandstoreBySlug(this.defaultBrandstoreSlug)
            ) {
                return;
            }

            if (this.retailerId) {
                this.preselectBrandstoreByRetailerId();
                return;
            }

            this.preselectIcelandDefault();
        },

        preselectIcelandDefault() {
            if (this.siteHandle == 'mgMotorsIceland') {
                const firstElement = Object.values(this.stores)[0];
                if (firstElement) {
                    this.selectedBrandStore = firstElement;
                    this.store = this.selectedBrandStore.title;
                }
            }
        },

        preselectBrandstoreBySlug(slug) {
            if (!slug || !this.stores) {
                return false;
            }

            const slugToMatch = String(slug);
            const matchingStore = Object.values(this.searchableStores).find(
                (store) => String(store.slug) == slugToMatch,
            );

            if (!matchingStore) {
                return false;
            }

            this.selectedBrandStore = matchingStore;
            this.store = matchingStore.title;
            return true;
        },

        async preselectBrandstoreByRetailerId() {
            const slug = await resolveBrandStoreSlugByRetailerId(
                this.retailerId,
                this.siteHandle,
            );

            // The user may have already picked a store or started typing while
            // this request was in flight - don't clobber their own input.
            if (this.selectedBrandStore || this.store) {
                return;
            }

            if (slug && this.preselectBrandstoreBySlug(slug)) {
                return;
            }

            this.preselectIcelandDefault();
        },

        handleAddErrorClass() {
            return this.isErrorOnStoreInput
                ? 'new-input__container--error'
                : '';
        },

        resetErrorState() {
            this.selectedBrandStore = null;
            this.showDropdown = false;
            this.needMoreInput = false;
            this.cityNotFound = false;

            EventBus.$emit('brandstore-cleared');
        },

        isSelectedStoreSearchable() {
            if (!this.selectedBrandStore) {
                return false;
            }

            return Object.values(this.searchableStores).some(
                (store) =>
                    String(store.id) === String(this.selectedBrandStore.id),
            );
        },

        resetSearch() {
            this.store = '';
            this.zipCoords = null;
            this.nearbyProximityStores = null;
            this.resetErrorState();

            EventBus.$emit('brandstore-search-reset', this.inputId);
        },

        setCityNotFound() {
            this.showDropdown = false;
            this.needMoreInput = false;
            this.cityNotFound = true;
        },

        initMap() {
            // Adding Google maps Geocoder
            this.geocoder = new google.maps.Geocoder();
        },

        setInputLengthError() {
            this.needMoreInput = true;
            this.nearbyProximityStores = null;
        },

        injectGoogleMaps() {
            const mountedMapsScript = document.querySelector(
                'script[src="//maps.google.com/maps/api/js?key=AIzaSyBXLteX7ErcVvjvFLHB1_TMCzq55wVb5Bg&libraries=places&callback=mapsCallback"]',
            );
            if (mountedMapsScript) return;

            let tag = document.createElement('script');
            tag.setAttribute('type', 'text/javascript');
            tag.defer = true;
            tag.async = true;
            tag.setAttribute(
                'src',
                '//maps.google.com/maps/api/js?key=AIzaSyBXLteX7ErcVvjvFLHB1_TMCzq55wVb5Bg&libraries=places&callback=mapsCallback',
            );
            (
                document.getElementsByTagName('head')[0] ||
                document.documentElement
            ).appendChild(tag);
        },

        searchByZipGoogleApi: _debounce(function () {
            this.makeApiRequest();
        }, 300),

        makeApiRequest() {
            if (!this.store) {
                this.apiRequestLoading = false;
                return;
            }

            this.apiRequestLoading = true;
            const requestTerritory = this.territory;

            let tempStore = this.store;

            let code = this.countryValue[this.siteHandle];

            if (this.siteHandle === 'mgMotorsFrance') {
                ({ address: tempStore, country: code } =
                    resolveFrenchGeocodeParams(this.store));
            }

            const territoryCountry = this.isTerritoryScoped
                ? getFrenchTerritoryCountry(this.territory)
                : undefined;
            if (territoryCountry) {
                code = territoryCountry;
            }

            if (this.siteHandle.includes('Switzerland')) {
                code = this.countryValue[this.siteHandle];
            }

            if (this.siteHandle === 'mgMotorsNorway') {
                code = 'NO';

                // Svalbard and Jan Mayen
                const locations = [
                    { code: '8099', name: 'Jan Mayen' },
                    { code: '9170', name: 'Longyearbyen' },
                    { code: '9171', name: 'Longyearbyen' },
                    { code: '9172', name: 'Isfjord' },
                    { code: '9173', name: 'Ny-Ålesund' },
                    { code: '9174', name: 'Hopen' },
                    { code: '9175', name: 'Sveagruva' },
                    { code: '9176', name: 'Bjørnøya' },
                    { code: '9177', name: 'Hornsund' },
                    { code: '9178', name: 'Barentsburg' },
                ];

                locations.forEach((location) => {
                    if (
                        location.code.includes(tempStore) ||
                        location.name.includes(tempStore)
                    ) {
                        code = 'SJ';
                        tempStore = `${location.code} ${location.name}`;
                    }
                });
            }

            const handleGeocodeResult = (results, status) => {
                if (requestTerritory !== this.territory) {
                    this.apiRequestLoading = false;
                    return;
                }

                if (status === 'OK' && results.length) {
                    const [res] = results;
                    this.apiRequestLoading = false;
                    const isDefaultLocation =
                        res.address_components.length === 1 &&
                        res.address_components[0].short_name === code;

                    //check if geocoder returns default coordination - no results for search
                    if (this.siteHandle !== 'mgMotorsNorway') {
                        if (isDefaultLocation) {
                            this.setCityNotFound();
                            return;
                        }
                    }

                    if (
                        this.isTerritoryScoped &&
                        !isGeocodeResultInFrenchTerritory(res, this.territory)
                    ) {
                        this.setCityNotFound();
                        return;
                    }

                    this.showDropdown = true;

                    this.zipCoords = {
                        lat: res.geometry.location.lat(),
                        lng: res.geometry.location.lng(),
                    };

                    if (!this.zipCoords || this.stores.length < 1) return;

                    this.getNearbyBrandstores(
                        this.zipCoords,
                        this.searchableStores,
                    );
                    return;
                }

                this.apiRequestLoading = false;
                this.setCityNotFound();
                console.error(
                    'Geocode was not successful for the following reason: ' +
                        status,
                );
            };

            if (territoryCountry) {
                this.geocoder.geocode(
                    {
                        address: tempStore,
                        componentRestrictions: { country: code },
                    },
                    handleGeocodeResult,
                );
                return;
            }

            geocodeWithFrenchFallback(
                this.geocoder,
                { address: tempStore, country: code },
                this.siteHandle,
                handleGeocodeResult,
            );
        },

        getNearbyBrandstores(userLocation, brandstores) {
            const storesWithDistance = Object.values(brandstores).map(
                (store) => {
                    const distance = this.getDistance(userLocation, store);
                    return {
                        ...store,
                        distance,
                    };
                },
            );

            this.cityNotFound = false;

            this.nearbyProximityStores = storesWithDistance
                .sort((a, b) => {
                    if (a.distance < b.distance) {
                        return -1;
                    }

                    if (a.distance > b.distance) {
                        return 1;
                    }

                    return 0;
                })
                .slice(0, this.brandstoreResultAmount);
        },

        getDistance(userLocation, brandstore) {
            // Bron: https://cloud.google.com/blog/products/maps-platform/how-calculate-distances-map-maps-javascript-api
            var radius = 6371.071; // Radius of the Earth in KM

            const radiusLat1 = userLocation.lat * (Math.PI / 180); // Convert degrees to radians
            const radiusLat2 = brandstore.lat * (Math.PI / 180); // Convert degrees to radians
            const differenceLat = radiusLat2 - radiusLat1; // Radian difference (latitudes)
            const differenceLng =
                (brandstore.lng - userLocation.lng) * (Math.PI / 180); // Radian difference (longitudes)

            const distance =
                2 *
                radius *
                Math.asin(
                    Math.sqrt(
                        Math.sin(differenceLat / 2) *
                            Math.sin(differenceLat / 2) +
                            Math.cos(radiusLat1) *
                                Math.cos(radiusLat2) *
                                Math.sin(differenceLng / 2) *
                                Math.sin(differenceLng / 2),
                    ),
                );

            return distance;
        },

        replaceInputValue(e) {
            e.preventDefault();
            this.selectedBrandStore = this.stores[e.target.dataset.value];
            this.store = this.selectedBrandStore.title;

            // Reset zipCoords to null
            this.zipCoords = null;
        },
    },
});
